import { useEffect, useRef, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { FormTabs } from '@/components/ui/form-tabs';
import useMenu from '@/hooks/use-menu';
import { cn } from '@/lib/cn';
import type { IMenu } from '@/types/menu';

type SectionTab = Omit<IMenu, 'icon' | 'children' | 'tabs'>;

/** Consecutive tabs sharing a `group`, in menu order. */
const groupTabs = (tabs: SectionTab[]) =>
  tabs.reduce<{ label: string; tabs: SectionTab[] }[]>((groups, tab) => {
    const label = tab.group ?? '';
    const last = groups.at(-1);
    if (last && last.label === label) last.tabs.push(tab);
    else groups.push({ label, tabs: [tab] });
    return groups;
  }, []);

/**
 * Grouped nav for a section with more pages than a tab strip holds (Settings
 * → Company). A column beside the panel on a wide screen; below `lg` the same
 * groups run as one row that scrolls sideways, so every page stays one tap
 * away and keeps its group heading for context.
 */
const SectionNav = ({
  tabs,
  activeRoute,
}: {
  tabs: SectionTab[];
  activeRoute: string;
}) => {
  const navRef = useRef<HTMLElement>(null);

  // In the phone row the open page can sit past the right edge: bring it in.
  // Only the row scrolls, never the document, and a column has nothing to do.
  useEffect(() => {
    const nav = navRef.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !active || nav.scrollWidth <= nav.clientWidth) return;
    nav.scrollLeft = active.offsetLeft - 16;
  }, [activeRoute]);

  return (
    <nav
      ref={navRef}
      aria-label="Section"
      className="relative flex shrink-0 gap-6 overflow-x-auto pb-2 lg:sticky lg:top-6 lg:w-48 lg:flex-col lg:gap-5 lg:overflow-visible lg:pb-0"
    >
      {groupTabs(tabs).map(group => (
        <div
          key={group.label}
          className="flex shrink-0 items-center gap-1 lg:flex-col lg:items-stretch"
        >
          {group.label && (
            <p className="text-text-primary px-3 text-xs font-semibold tracking-wide whitespace-nowrap uppercase lg:pb-1">
              {group.label}
            </p>
          )}
          {group.tabs.map(tab => {
            const isActive = tab.route === activeRoute;
            return (
              <Link
                key={tab.route}
                to={tab.route}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'text-text-tertiary hover:bg-primary-10 hover:text-base-orange-40 rounded-lg px-3 py-2 text-sm whitespace-nowrap transition-colors focus-visible:ring-1 focus-visible:ring-neutral-50 focus-visible:outline-none',
                  isActive && 'bg-primary-10 text-base-orange-40 font-medium'
                )}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      ))}
    </nav>
  );
};

/**
 * The tab strip for a menu marked `tabs` (Settings → Company), and the panel
 * it sits on: the same `FormTabs` rail and card the other tabbed screens use
 * (Smart Suggestion, the gift card form), so these read as the same app. One
 * tab per child the role can view. When the children carry a `group`, they
 * render as a grouped side nav instead (`SectionNav`): past a handful of
 * pages a tab rail outgrows the page.
 *
 * Each tab is its child's own route rather than in-page state, so a tab can
 * be linked to, the back button walks between them, and every page keeps its
 * permission check.
 *
 * Drawn on the tab pages themselves only: a tab's sub-pages (a reseller's
 * form, a location's form) carry their own back navigation, and a tab strip
 * above an open form would invite leaving it mid-edit. Anything else renders
 * `children` untouched.
 */
const SectionTabsFrame = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { currentParent, getVisibleTabs } = useMenu();

  const tabs = getVisibleTabs(currentParent);
  const activeIndex = tabs.findIndex(tab => tab.route === pathname);
  if (activeIndex < 0) return children;

  if (tabs.some(tab => tab.group)) {
    return (
      <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        <SectionNav tabs={tabs} activeRoute={pathname} />
        <div className="min-w-0 flex-1 rounded-2xl bg-neutral-100 p-6">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col">
      {/* The rail is as wide as its tabs; on a phone six of them outrun the
          screen, so it scrolls sideways rather than widening the page. */}
      <div className="overflow-x-auto">
        <FormTabs
          activeTab={activeIndex}
          onChangeTab={index => navigate(tabs[index].route)}
          tabs={tabs.map((tab, index) => ({
            id: index,
            label: tab.label,
          }))}
        />
      </div>

      <div className="rounded-2xl rounded-tl-none bg-neutral-100 p-6">
        {children}
      </div>
    </div>
  );
};

export { SectionTabsFrame };
